/**
 * @file 布局块
 */

import {generateBlockId, isStringOrNull} from "../utils";
import {ExtendedNodeClass, ParagraphClass, TableClass} from "./index";
import {AllowedBlockChild, AllowedTableChild, ColorOnlyTransparent, MpNode} from "../types";
import {BlockColorMap} from '../constants';

// 布局块
export class BlockClass implements MpNode {
    private readonly children: Array<BlockColClass | BlockItemClass>;
    private readonly widths?: number[];
    constructor(options?: {widths?: number[]} | null, children?: Array<BlockColClass | BlockItemClass>) {
        options = options || {};
        this.widths = options.widths;
        this.children = children ?? [new BlockItemClass()];
    }
    
    toJson() {
        const block = new BlockRowClass({widths: this.widths}, this.children);
        const cellList: BlockItemClass[] = [];
        const root = this.buildTree(block, cellList);
        return {
            type: 'layout-group',
            blockId: generateBlockId(),
            blockTree: root,
            view: 'block',
            children: cellList.map(child => child.toJson()),
        };
    }
    
    /**
     * 构建布局树blockTree，将其中的叶子节点提取到cellList中
     * @param block
     * @param cellList
     * @param totalWidth
     * @private
     */
    private buildTree(block: BlockColClass | BlockRowClass, cellList: BlockItemClass[], totalWidth: number = 1000) {
        if (!Array.isArray(block.children) || block.children.length === 0) {
            return block.toJson();
        }
        // 只有一个子节点的时候直接移除子节点；只出现在根节点Row下只有一个Item节点的情况：Block({children: [Block.Item()]})
        if (this.isRow(block) && block.children.length === 1) {
            const cell = block.children[0] as BlockItemClass;
            cellList.push(cell);
            block.setMinHeight(cell.minHeight);
            block.setLayoutId(cell.layoutId!);
            delete cell.minHeight;
            return block.toJson();
        }
        // Note: 第一次遍历只记录叶子节点位置，在最后递归buildTree时再将叶子节点推到cellList中，这样保证dfs叶子节点顺序的正确性
        const leafNodes: Array<BlockItemClass | null> = [];
        // 将所有叶子子节点替换为行或列节点
        block.children.forEach((child, idx) => {
            if (child instanceof BlockItemClass) {
                leafNodes.push(child);
                block.children![idx] = this.isRow(block)
                    ? new BlockColClass({layoutId: child.layoutId})
                    : new BlockRowClass({layoutId: child.layoutId, minHeight: child.minHeight});
                delete child.minHeight;
            }
            else {
                leafNodes.push(null);
            }
        });
        // 为行节点下的所有列节点赋予宽度
        if (this.isRow(block)) {
            const widths = block.widths || this.averageWidth(totalWidth, block.children.length);
            widths.forEach(width => {
                if (!Number.isInteger(width)) {
                    throw new Error(`width must be integer：[${JSON.stringify(widths)}]`);
                }
            })
            block.children.forEach((child: BlockColClass, idx) => {
                child.setWidth(widths[idx]);
            });
        }
        const root = block.toJson();
        root.children = [];
        // 构建json结构
        block.children.forEach((child, idx) => {
            root.children[idx] = this.buildTree(child, cellList, this.isRow(block) ? (child as BlockColClass).width : totalWidth);
            if (leafNodes[idx]) {
                cellList.push(leafNodes[idx]!);
            }
        });
        return root;
    }
    
    private isRow(block: BlockColClass | BlockRowClass): block is BlockRowClass {
        return block instanceof BlockRowClass;
    }
    
    /**
     * 将一份宽度均分切割为整数值列表，最后一项取余
     * @param totalWidth
     * @param cut
     * @private
     */
    private averageWidth(totalWidth: number, cut: number) {
        if (cut < 2) {
            throw new Error(`cut must be greater than 1：[${JSON.stringify(totalWidth)}]`);
        }
        let total = 0;
        return Array.from({length: cut}, (_, i) => {
            if (i === cut - 1) {
                return totalWidth - total;
            }
            const average = Math.floor(totalWidth / cut);
            total += average;
            return average;
        });
    }
}
// 布局块行
export class BlockRowClass implements MpNode {
    readonly children?: Array<BlockColClass | BlockItemClass>;
    readonly widths?: number[];
    private minHeight?: number;
    private layoutId?: string;
    constructor(options: {
        layoutId?: string;
        minHeight?: number;
        widths?: number[];
    } | null, children?: Array<BlockColClass | BlockItemClass>
    ) {
        options = options || {};
        if (Array.isArray(options.widths) && options.widths.length !== children?.length) {
            throw new Error(`widths length must equal children：[${options.widths.join(',')}]`);
        }
        this.minHeight = options.minHeight;
        this.layoutId = options.layoutId;
        this.widths = options.widths;
        this.children = children;
    }
    setLayoutId(layoutId: string) {
        this.layoutId = layoutId;
    }
    setMinHeight(minHeight?: number) {
        this.minHeight = minHeight;
    }
    toJson() {
        const root: any = {
            type: 'row',
            layoutId: this.layoutId || generateBlockId(),
        };
        if (this.minHeight && this.minHeight > 0) {
            root.minHeight = this.minHeight;
        }
        return root;
    }
}
// 布局块列
export class BlockColClass implements MpNode {
    readonly children?: Array<BlockRowClass | BlockItemClass>;
    width?: number;
    private readonly layoutId?: string;
    constructor(options: {layoutId?: string; width?: number} | null, children?: Array<BlockRowClass | BlockItemClass>) {
        options = options || {};
        this.width = options.width;
        this.layoutId = options.layoutId;
        this.children = children;
    }
    setWidth(width: number) {
        this.width = width;
    }
    toJson() {
        return {
            type: 'col',
            width: this.width,
            layoutId: this.layoutId || generateBlockId(),
        };
    }
}
// 布局块项
export interface BlockItemOptions {
    bgColor?: ColorOnlyTransparent;
    borderColor?: ColorOnlyTransparent;
    minHeight?: number;
    verticalAlign?: 'top' | 'middle' | 'bottom';
}
export class BlockItemClass implements MpNode {
    minHeight?: number;
    private readonly children: AllowedBlockChild[];
    readonly layoutId?: string;
    private readonly bgColor?: ColorOnlyTransparent;
    private readonly borderColor?: ColorOnlyTransparent;
    private readonly verticalAlign?: BlockItemOptions['verticalAlign'];
    
    constructor(options?: BlockItemOptions | null, children?: string | AllowedBlockChild[]) {
        this.layoutId = generateBlockId();
        this.bgColor = options?.bgColor;
        this.borderColor = options?.borderColor;
        this.minHeight = options?.minHeight;
        this.verticalAlign = options?.verticalAlign;
        this.children = typeof children === 'string'
            ? [new ParagraphClass(null, children)]
            : (children || [new ParagraphClass()]);
    }
    toJson() {
        return {
            type: 'layout-cell',
            blockId: generateBlockId(),
            layoutId: this.layoutId,
            theme: BlockColorMap[this.bgColor!],
            borderTheme: BlockColorMap[this.borderColor!],
            verticalAlign: this.verticalAlign,
            children: this.children.map(child => child.toJson()),
        };
    }
}
